import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch } from 'react-redux';

import { obtenerPaquete } from '../../../actions/configuracion';
import { cargarDetalleProductoVenta } from '../../../actions/ventas';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicacionLabel = (inv) => {
    const parts = [];
    if (inv?.area?.nombre) parts.push(inv.area.nombre);
    if (inv?.organizador?.nombre) parts.push(inv.organizador.nombre);
    if (inv?.ubicacion?.nombre) parts.push(inv.ubicacion.nombre);
    return parts.join(' / ');
};

/* Agregar un paquete a la venta: cada componente de la receta (paquetes_productos) descuenta
   su propio inventario en UNA ubicación elegida aquí — el backend no soporta dividir un
   componente entre varias ubicaciones, así que el selector es de una sola opción por renglón. */
export default function SeleccionPaqueteModal({ show, onHide, onConfirm, paquete, esCotizacion, moneda, tipoCambio }) {
    const dispatch = useDispatch();

    const [cargando, setCargando] = useState(false);
    const [receta, setReceta] = useState(null);
    const [inventariosPorProducto, setInventariosPorProducto] = useState({});

    const [cantidadPaquetes, setCantidadPaquetes] = useState(1);
    const [precioPaquete, setPrecioPaquete] = useState(0);
    const [ivaPaquete, setIvaPaquete] = useState(false);
    const [ubicacionesSel, setUbicacionesSel] = useState({});

    const menuPortalTarget = useMemo(() => {
        if (typeof document === 'undefined') return null;
        return document.body;
    }, []);

    const esUsd = Number(moneda) === 1;
    const tc = esUsd && Number(tipoCambio) > 0 ? Number(tipoCambio) : 1;
    const prefMoneda = esUsd ? 'USD $' : '$';

    useEffect(() => {
        if (!show || !paquete?._id) {
            setReceta(null);
            setInventariosPorProducto({});
            return;
        }

        const cargar = async () => {
            setCargando(true);

            const detalle = await dispatch(obtenerPaquete(paquete._id));
            const componentes = Array.isArray(detalle?.productos) ? detalle.productos : [];
            setReceta({
                _id: detalle?._id,
                nombre: detalle?.nombre,
                codigo: detalle?.codigo,
                componentes
            });

            if (!esCotizacion) {
                const mapa = {};
                await Promise.all(componentes.map(async (c) => {
                    const idProducto = c?.producto?._id;
                    if (!idProducto) return;
                    const detalleProducto = await dispatch(cargarDetalleProductoVenta(idProducto));
                    mapa[idProducto] = Array.isArray(detalleProducto?.inventarios) ? detalleProducto.inventarios : [];
                }));
                setInventariosPorProducto(mapa);
            }

            setCargando(false);
        };

        cargar();
    }, [show, paquete, esCotizacion, dispatch]);

    useEffect(() => {
        if (!show) return;

        setCantidadPaquetes(1);
        setPrecioPaquete(Number(((paquete?.precio || 0) / tc).toFixed(3)));
        setIvaPaquete(!!paquete?.iva);
        setUbicacionesSel({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, paquete, tc]);

    const componentes = Array.isArray(receta?.componentes) ? receta.componentes : [];

    const onChangeUbicacion = (productoId, opt) => {
        setUbicacionesSel((prev) => ({ ...prev, [productoId]: opt || null }));
    };

    const puedeConfirmar = (() => {
        if (cargando) return false;
        if (!Number(cantidadPaquetes) || Number(cantidadPaquetes) <= 0) return false;
        if (componentes.length === 0) return false;
        if (esCotizacion) return true;

        return componentes.every((c) => {
            const idProducto = c?.producto?._id;
            const sel = ubicacionesSel[idProducto];
            if (!sel?.raw) return false;

            const conversion = Number(c?.conversion || 1) || 1;
            const necesaria = (Number(c?.cantidad || 0) * Number(cantidadPaquetes || 0)) / conversion;
            const disponible = Number(sel.raw?.cantidad || 0);
            return disponible >= necesaria;
        });
    })();

    const handleConfirm = () => {
        if (!puedeConfirmar) return;

        const componentesPayload = componentes.map((c) => {
            const idProducto = c?.producto?._id;
            const sel = esCotizacion ? null : ubicacionesSel[idProducto];
            const conversion = Number(c?.conversion || 1) || 1;
            const necesaria = (Number(c?.cantidad || 0) * Number(cantidadPaquetes || 0)) / conversion;

            return {
                productoId: idProducto,
                productoNombre: c?.producto?.nombre || '',
                unidadNombre: c?.unidad?.nombre || '',
                cantidadPorPaquete: Number(c?.cantidad || 0),
                conversion,
                cantidadNecesaria: necesaria,
                areaId: sel?.raw?.area?._id || null,
                organizadorId: sel?.raw?.organizador?._id || null,
                ubicacionId: sel?.raw?.ubicacion?._id || null,
                ubicacionLabel: sel ? buildUbicacionLabel(sel.raw) : null
            };
        });

        onConfirm({
            paqueteId: receta._id,
            paqueteNombre: receta.nombre,
            paqueteCodigo: receta.codigo,
            cantidad: Number(cantidadPaquetes || 0),
            precio: Number(precioPaquete || 0),
            iva: ivaPaquete,
            componentes: componentesPayload
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Agregar paquete</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ overflow: 'visible' }}>
                <div className="mb-3">
                    <div className="fw-semibold">{paquete?.nombre || ''}</div>
                    {paquete?.codigo ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>{paquete.codigo}</div>
                    ) : null}
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-4">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Cantidad de paquetes</div>
                        <Form.Control
                            type="number"
                            value={cantidadPaquetes}
                            min={1}
                            onChange={(e) => setCantidadPaquetes(e.target.value === '' ? '' : Number(e.target.value))}
                        />
                    </div>
                    <div className="col-md-4">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio del paquete</div>
                        <CurrencyInput
                            value={precioPaquete}
                            decimalsLimit={3}
                            allowNegativeValue={false}
                            className="form-control"
                            prefix={`${prefMoneda} `}
                            groupSeparator=","
                            decimalSeparator="."
                            onValueChange={(value) => setPrecioPaquete(value === undefined || value === null || value === '' ? 0 : Number(value))}
                        />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                        <Form.Check
                            type="checkbox"
                            label="IVA"
                            checked={ivaPaquete}
                            onChange={() => setIvaPaquete(!ivaPaquete)}
                        />
                    </div>
                </div>

                {cargando ? (
                    <div className="text-muted text-center py-3">Cargando receta del paquete...</div>
                ) : (
                    <div className="table-responsive">
                        <Table bordered hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th style={{ width: 110 }} className="text-center">Cantidad</th>
                                    {!esCotizacion && <th style={{ width: 260 }}>Ubicación</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {componentes.length === 0 && (
                                    <tr>
                                        <td colSpan={esCotizacion ? 2 : 3} className="text-center text-muted">
                                            Este paquete no tiene productos configurados
                                        </td>
                                    </tr>
                                )}

                                {componentes.map((c) => {
                                    const idProducto = c?.producto?._id;
                                    const conversion = Number(c?.conversion || 1) || 1;
                                    const necesaria = (Number(c?.cantidad || 0) * Number(cantidadPaquetes || 0)) / conversion;
                                    const inventarios = inventariosPorProducto[idProducto] || [];
                                    const opcionesUbicacion = inventarios.map((inv) => ({
                                        value: inv._id,
                                        label: `${buildUbicacionLabel(inv) || 'Sin ubicación'} · Disp: ${formatNumber(inv?.cantidad)}`,
                                        raw: inv
                                    }));
                                    const sel = ubicacionesSel[idProducto] || null;
                                    const faltaStock = !esCotizacion && sel && Number(sel.raw?.cantidad || 0) < necesaria;

                                    return (
                                        <tr key={idProducto} className={faltaStock ? 'table-danger' : ''}>
                                            <td className="align-middle">
                                                <div className="fw-semibold">{c?.producto?.nombre || 'Sin definir'}</div>
                                                {c?.producto?.codigo ? (
                                                    <div className="text-muted" style={{ fontSize: 12 }}>{c.producto.codigo}</div>
                                                ) : null}
                                            </td>
                                            <td className="text-center align-middle">
                                                {formatNumber(necesaria)} {c?.unidad?.nombre || ''}
                                            </td>
                                            {!esCotizacion && (
                                                <td>
                                                    {opcionesUbicacion.length > 0 ? (
                                                        <Select
                                                            placeholder="Selecciona ubicación"
                                                            value={sel}
                                                            onChange={(opt) => onChangeUbicacion(idProducto, opt)}
                                                            options={opcionesUbicacion}
                                                            menuPortalTarget={menuPortalTarget}
                                                            menuPosition="fixed"
                                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                        />
                                                    ) : (
                                                        <span className="text-danger" style={{ fontSize: 13 }}>Sin existencia</span>
                                                    )}
                                                    {faltaStock && (
                                                        <div className="text-danger mt-1" style={{ fontSize: 11 }}>
                                                            Stock insuficiente para la cantidad de paquetes
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {esCotizacion && (
                    <div className="text-muted mt-3" style={{ fontSize: 13 }}>
                        La ubicación de cada producto del paquete se definirá hasta convertir la cotización en venta.
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirm} disabled={!puedeConfirmar}>
                    Agregar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
